import { DateColumnHeader } from '../components/DateColumnHeader';
import { formatAppDate, formatAppDateTime } from '../utility/dateLocale';
import { invoiceInstallmentNumbers } from '../utility/invoiceOpportunityRows';
import { InvoiceMonthFilter, invoiceMonthValue } from '../components/InvoiceMonthFilter';
import { IconSearch } from '@tabler/icons-react';
import { InvoiceStatusBadge } from '../components/InvoiceStatusBadge';
import { InvoiceOpportunityTable } from '../components/InvoiceOpportunityTable';
import { matchesInvoiceFilters } from '../utility/invoiceFilters';
import { isInvoiceAttachment, resolveInvoiceStatus, type InvoiceStatus } from '../utility/invoiceStatus';
import { InvoiceKanbanView } from '../components/InvoiceKanbanView';
import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildMrtLocalization } from '../i18n/mrtLocale';
import { Modal, Group, Title, Text, ScrollArea, Divider, Stack, Button, Textarea, Select, MultiSelect, RangeSlider, NumberInput, Table, Loader, SegmentedControl, TextInput } from '@mantine/core';
import { AttachmentActionButtons } from '../components/AttachmentActionButtons';
import { useAttachmentPreview } from '../components/AttachmentPreviewModal';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { type MRT_ColumnDef, type MRT_Row } from 'mantine-react-table';
import { useExportableTable } from '../utility/useExportableTable';
import { enableGlobalSearchOnColumns } from '../utility/mrtTableSearch';
import { ImportConfirmModal } from '../components/ImportConfirmModal';
import { TableExcelImportModal } from '../components/TableExcelImportModal';
import { PageFilterBanner } from '../components/PageFilterBanner';
import { readRowCell } from '../utility/excelImportHelpers';
import { pickRowId, readExcelFile, splitImportRowsById } from '../utility/tableExcelExport';
import { MRT_DensityState } from 'mantine-react-table';
import Environment from '../utility/environment';
import { useAuth } from '../contexts/AuthProvider';
import { Options } from '../utility/types';
import { useMrtTableContainerProps, usePageTableShellStyle } from '../utility/mobilePageLayout';
import { downloadAttachment } from '../utility/openRemoteFile';
import { buildPageUrl, readPageFilterParam } from '../utility/pageNavigationFilters';

type InvoiceAttachment = {
  id: string;
  invoice_id: string;
  file_name: string;
  file_url: string;
  uploaded_at: string;
};

type TaxIdPrompt = {
  status: 'mismatch' | 'missing_client';
  parsedTaxId: string;
  clientTaxId: string;
};

type InvoiceInstallment = {
  parcela_num: number;
  data: string;
  amount: number;
};

type InvoiceLineItem = {
  opportunity_product_id: string;
  product_id: string;
  payment_id: string;
  product_name: string;
  name: string;
  quantity: number;
  deal_price: number;
  discount: number;
  iva: number;
  currency: string;
  line_total: number;
  amount_on_invoice: number;
  payment_type: string;
  data_avista?: string | null;
  parcela_num_on_invoice?: number | null;
  amount?: number;
  installments_count?: number | null;
  installments?: InvoiceInstallment[] | null;
};

type Invoice = {
  id: string;
  client_id: string;
  opportunity_id: string;
  name: string;
  issue_date: string;
  total_amount: number;
  paid_amount: number;
  invoice_status: InvoiceStatus;
  currency: string;
  exchange_rate: number | null;
  remito: string;
  client_name: string;
  client_tax_id: string;
  client_iva: string;
  client_address: string;
  client_city: string;
  client_state: string;
  client_country: string;
  client_zipcode: string;
  notes: string;
  has_attachments: boolean;
  has_factura_attachment: boolean;
  line_items: InvoiceLineItem[];
};

function EntityLink({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Text
      component="span"
      sx={(theme) => ({
        color: theme.colors.blue[6],
        cursor: 'pointer',
        '&:hover': { textDecoration: 'underline' },
      })}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
    >
      {label}
    </Text>
  );
}

function usdAmount(usd: number): string {
  return `USD ${usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Converted Value using the issue-date BNA rate: dash when no rate is available. */
function convertedAmount(usd: number, currency: string, factor: number | null): string {
  const cur = currency || 'USD';
  if (cur === 'USD') return usdAmount(usd);
  if (!factor) return '—';
  return `${cur} ${(usd * factor).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatIssueDateForBna(iso: string): string | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

function buildRateDescription(
  t: TFunction,
  currency: string,
  rate: number | null,
  isManual: boolean,
  bnaFecha: string | null,
  bnaFetchDone: boolean,
  issueInFuture: boolean,
): string {
  if (isManual) return t('invoices.rateManual', { currency });
  if (rate != null) {
    return bnaFecha
      ? t('invoices.rateBna', { currency, date: bnaFecha })
      : t('invoices.rateSimple', { currency });
  }
  if (issueInFuture) return t('invoices.rateFutureDate');
  if (bnaFetchDone) return t('invoices.rateUnavailable');
  return t('invoices.rateFetching');
}

function isIssueDateInFuture(iso: string): boolean {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const issue = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return issue.getTime() > today.getTime();
}

const tableSx = {
  display: 'table',
  tableLayout: 'auto' as const,
  borderCollapse: 'separate' as const,
  '& thead': { position: 'sticky' as const, top: 0, zIndex: 100, backgroundColor: 'rgba(48, 113, 91, 0.9)', color: '#fff' },
};

const UUID_KEY_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function normalizeInvoicesPayload(payload: unknown): Invoice[] {

  if (!payload || typeof payload !== 'object') return [];

  const payloadAsObject = payload as Record<string, unknown>;
  const rawInvoices =
    payloadAsObject.invoices && typeof payloadAsObject.invoices === 'object' && !Array.isArray(payloadAsObject.invoices)
      ? (payloadAsObject.invoices as Record<string, unknown>)
      : payloadAsObject;
  const numInstallmentsByPaymentId =
    payloadAsObject.num_installments && typeof payloadAsObject.num_installments === 'object' && !Array.isArray(payloadAsObject.num_installments)
      ? (payloadAsObject.num_installments as Record<string, unknown>)
      : {};

  const invoices = Object.entries(rawInvoices)
    .map(([invoiceId, raw]) => {
      if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
      const inv = raw as Record<string, unknown>;

      const rawItems = inv.items;
      const lineItemsSource =
        rawItems && typeof rawItems === 'object' && !Array.isArray(rawItems)
          ? (rawItems as Record<string, unknown>)
          : inv;

      const lineItems: InvoiceLineItem[] = [];
      for (const [lineKey, lineRaw] of Object.entries(lineItemsSource)) {
        if (!UUID_KEY_RE.test(lineKey)) continue;
        if (!lineRaw || typeof lineRaw !== 'object' || Array.isArray(lineRaw)) continue;
        const line = lineRaw as Record<string, unknown>;
        const dealPrice = Number(line.deal_price);
        const quantity = Number(line.quantity) || 1;
        const discount = Number(line.discount) || 0;
        const lineTotal = dealPrice * quantity * (1 - discount / 100);

        lineItems.push({
          opportunity_product_id: String(line.opp_product_id ?? lineKey),
          product_id: String(line.product_id ?? ''),
          payment_id: String(line.payment_id ?? ''),
          product_name: String(line.name ?? line.product_name ?? line.product_id ?? ''),
          name: String(line.name ?? ''),
          quantity,
          deal_price: dealPrice,
          discount,
          iva: line.iva != null ? Number(line.iva) : 0,
          currency: String(line.currency ?? 'USD'),
          line_total: lineTotal,
          amount_on_invoice: lineTotal,
          payment_type: String(line.tipo ?? 'avista'),
          data_avista: line.data_avista != null ? String(line.data_avista) : null,
          parcela_num_on_invoice: line.parcela_num != null ? Number(line.parcela_num) : null,
          amount: line.amount != null && line.amount !== '' ? Number(line.amount) : undefined,
          installments_count: null,
          installments: null,
        });
      }

      return {
        id: invoiceId,
        client_id: String(inv.client_id ?? ''),
        opportunity_id: String(inv.opportunity_id ?? ''),
        name: String(inv.name ?? ''),
        issue_date: String(inv.issue_date ?? ''),
        total_amount: Number(inv.total_amount),
        paid_amount: Number(inv.paid_amount ?? 0),
        invoice_status: resolveInvoiceStatus(String(inv.issue_date ?? ''), Boolean(inv.has_invoice_attachment ?? inv.has_factura_attachment), String(inv.invoice_status ?? 'pending')),
        currency: String(inv.currency ?? 'USD'),
        exchange_rate: inv.exchange_rate != null && inv.exchange_rate !== '' ? Number(inv.exchange_rate) : null,
        remito: String(inv.remito ?? ''),
        client_name: String(inv.client_name ?? ''),
        client_tax_id: String(inv.client_tax_id ?? ''),
        client_iva: String(inv.client_iva ?? ''),
        client_address: String(inv.client_address ?? ''),
        client_city: String(inv.client_city ?? ''),
        client_state: String(inv.client_state ?? ''),
        client_country: String(inv.client_country ?? ''),
        client_zipcode: String(inv.client_zipcode ?? ''),
        notes: String(inv.notes ?? ''),
        has_attachments: Boolean(inv.has_attachments),
        has_factura_attachment: Boolean(inv.has_invoice_attachment ?? inv.has_factura_attachment),
        line_items: lineItems,
      } satisfies Invoice;
    })
    .filter((row): row is Invoice => row != null);

  const installmentCountByOppProduct: Record<string, number> = {};
  for (const invoice of invoices) {
    for (const line of invoice.line_items) {
      if (line.payment_type !== 'parcelado') continue;
      const installmentsCountFromApi = Number(numInstallmentsByPaymentId[line.payment_id] ?? 0);
      if (installmentsCountFromApi > 0) {
        installmentCountByOppProduct[line.opportunity_product_id] = installmentsCountFromApi;
        continue;
      }
      const installmentNum = Number(line.parcela_num_on_invoice ?? 0);
      installmentCountByOppProduct[line.opportunity_product_id] = Math.max(
        installmentCountByOppProduct[line.opportunity_product_id] ?? 0,
        installmentNum
      );
    }
  }

  for (const invoice of invoices) {
    for (const line of invoice.line_items) {
      if (line.payment_type !== 'parcelado') {
        line.amount_on_invoice = line.line_total;
        line.installments_count = null;
        continue;
      }
      const installmentsCount = installmentCountByOppProduct[line.opportunity_product_id] || 1;
      line.installments_count = installmentsCount;
      if (line.amount != null && !Number.isNaN(line.amount)) {
        line.amount_on_invoice = line.amount;
      } else {
        line.amount_on_invoice = line.line_total / installmentsCount;
      }
    }
  }

  return invoices;
}

export default function InvoicePage() {
  const { t, i18n } = useTranslation();
  const { accessToken, actualEnterprise, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const tableShellStyle = usePageTableShellStyle();
  const mrtTableContainerProps = useMrtTableContainerProps();
  const opportunityIdFilter = readPageFilterParam(location.search, 'opportunityId');
  const invoiceIdFilter = readPageFilterParam(location.search, 'invoiceId');
  const openedInvoiceFromUrl = useRef(false);

  const mrtLocalization = useMemo(
    () => buildMrtLocalization(t),
    [t, i18n.language],
  );

  const statusOptions = useMemo(
    () => [
      { value: 'pending', label: t('invoices.statusOptions.pending') },
      { value: 'complete', label: t('invoices.statusOptions.complete') },
      { value: 'delayed', label: t('invoices.statusOptions.delayed') },
      { value: 'canceled', label: t('invoices.statusOptions.canceled') },
    ],
    [t, i18n.language],
  );
  const [list, setList] = useState<Invoice[]>([]);
  useEffect(() => {
    const refresh = () => {
      const update = (inv: Invoice): Invoice => ({ ...inv, invoice_status: resolveInvoiceStatus(inv.issue_date, inv.has_factura_attachment, inv.invoice_status) });
      setList(cur => cur.map(update));
      setView(cur => cur ? update(cur) : cur);
    };
    const timer = window.setInterval(refresh, 60000);
    window.addEventListener('focus', refresh);
    return () => { window.clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, []);
  const [selectedMonth, setSelectedMonth] = useState(() => invoiceMonthValue(new Date()));
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [clientFilter, setClientFilter] = useState<string[]>([]);
  const [productFilter, setProductFilter] = useState<string[]>([]);
  const [minValueFilter, setMinValueFilter] = useState<number | ''>('');
  const [maxValueFilter, setMaxValueFilter] = useState<number | ''>('');
  const valueRangeMax = useMemo(() => Math.max(1, Math.ceil(list.reduce((max, invoice) => Number.isFinite(invoice.total_amount) ? Math.max(max, invoice.total_amount) : max, 0))), [list]);
  const valueRange: [number, number] = [minValueFilter === '' ? 0 : Math.min(minValueFilter, valueRangeMax), maxValueFilter === '' ? valueRangeMax : Math.min(maxValueFilter, valueRangeMax)];
  const formatRangeValue = (value: number) => new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value);
  const [ownerFilter, setOwnerFilter] = useState<string | null>(null);
  const [opportunityOwners, setOpportunityOwners] = useState<{ id: string; owner: string | null; owner_name: string | null }[]>([]);
  const [ownerLoadError, setOwnerLoadError] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'kanban'>('table');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [clients, setClients] = useState<Options[]>([]);
  const [view, setView] = useState<Invoice | null>(null);
  const [notesValue, setNotesValue] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [sendingInvoiceId, setSendingInvoiceId] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [issueRate, setIssueRate] = useState<number | null>(null);
  const [issueRateFecha, setIssueRateFecha] = useState<string | null>(null);
  const [viewCurrency, setViewCurrency] = useState<string>('ARS');
  const [rateOverride, setRateOverride] = useState<number | null>(null);
  const [bnaFetchAttempted, setBnaFetchAttempted] = useState(false);
  const [importModalOpened, setImportModalOpened] = useState(false);
  const [autoImportFile, setAutoImportFile] = useState<File | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [importNotification, setImportNotification] = useState<{ message: string; color: 'green' | 'red' } | null>(null);
  const [importConfirm, setImportConfirm] = useState<{
    updateCount: number;
    run: () => Promise<void>;
  } | null>(null);
  const [invoiceFiles, setInvoiceFiles] = useState<InvoiceAttachment[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [attachmentErrors, setAttachmentErrors] = useState<string[]>([]);
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState<string | null>(null);
  const { openPreview, previewModal } = useAttachmentPreview(accessToken);
  const [taxIdPrompt, setTaxIdPrompt] = useState<TaxIdPrompt | null>(null);
  const [updatingTaxId, setUpdatingTaxId] = useState(false);

  useEffect(() => {
    setIssueRate(null);
    setIssueRateFecha(null);
    setBnaFetchAttempted(false);
    if (!view || !accessToken) return;
    if (!viewCurrency || viewCurrency === 'USD') return;
    if (isIssueDateInFuture(view.issue_date)) return;
    const fecha = formatIssueDateForBna(view.issue_date);
    if (!fecha) return;
    const controller = new AbortController();
    const h = { Authorization: `Bearer ${accessToken}` };
    fetch(
      `${Environment.VITE_API_URL}/api/v1/exchange_rate/ars?fecha=${encodeURIComponent(fecha)}`,
      { headers: h, credentials: 'include', signal: controller.signal }
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return;
        setIssueRate(Number(data.rate));
        setIssueRateFecha(String(data.fecha ?? ''));
      })
      .catch(() => {})
      .finally(() => setBnaFetchAttempted(true));
    return () => controller.abort();
  }, [view, viewCurrency, accessToken]);

  const effectiveRate = rateOverride ?? issueRate;
  const isManualRate = rateOverride != null;

  useEffect(() => {
    document.title = t('invoices.documentTitle');
  }, [t, i18n.language]);

  useEffect(() => {
    const id = actualEnterprise?.id;
    if (!id || !accessToken) return;
    const h = { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` };
    fetch(`${Environment.VITE_API_URL}/api/v1/clients_options/${id}`, { headers: h, credentials: 'include' })
      .then((r) => r.json())
      .then((data) =>
        setClients(
          data.map((c: { id: string; legal_name: string }) => ({ value: c.id, label: c.legal_name })).sort((a: Options, b: Options) => a.label.localeCompare(b.label))
        )
      )
      .catch(console.error);
  }, [accessToken, actualEnterprise?.id]);

  useEffect(() => {
    const id = actualEnterprise?.id;
    if (!id || !accessToken) {
      setList([]);
      return;
    }
    setLoadError(null);
    const h = { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` };
    fetch(`${Environment.VITE_API_URL}/api/v1/invoices/${id}`, { headers: h, credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) {
          const t = await r.text();
          throw new Error(t || r.statusText);
        }
        return r.json() as Promise<unknown>;
      })
      .then((payload) => {
        setList(normalizeInvoicesPayload(payload));
      })
      .catch((e) => {
        console.error(e);
        setLoadError(e instanceof Error ? e.message : t('invoices.loadFailed'));
        setList([]);
      });
  }, [accessToken, actualEnterprise?.id, t]);

  useEffect(() => {
    setOwnerFilter(null);
    setSelectedMonth(invoiceMonthValue(new Date()));
    setStatusFilter([]); setClientFilter([]); setProductFilter([]);
    setMinValueFilter(''); setMaxValueFilter('');
    setOpportunityOwners([]);
    setOwnerLoadError(false);
    if (!actualEnterprise?.id || !accessToken) return;
    const controller = new AbortController();
    fetch(`${Environment.VITE_API_URL}/api/v1/opportunities/${actualEnterprise.id}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      credentials: 'include',
      signal: controller.signal,
    })
      .then(async response => {
        if (!response.ok) throw new Error(response.statusText);
        const data = await response.json();
        if (!Array.isArray(data)) throw new Error('Invalid opportunities response');
        if (!controller.signal.aborted) setOpportunityOwners(data);
      })
      .catch(() => {
        if (!controller.signal.aborted) setOwnerLoadError(true);
      });
    return () => controller.abort();
  }, [accessToken, actualEnterprise?.id]);

  const ownerOptions = useMemo(() => {
    const owners = new Map<string, string>();
    for (const opportunity of opportunityOwners) {
      if (opportunity.owner) owners.set(opportunity.owner, opportunity.owner_name || opportunity.owner);
    }
    return Array.from(owners, ([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [opportunityOwners]);

  const ownerNamesByOpportunity = useMemo(() => new Map(
    opportunityOwners.map(opportunity => [opportunity.id, opportunity.owner_name || ''])
  ), [opportunityOwners]);

  const ownerOpportunityIds = useMemo(() => new Set(
    opportunityOwners.filter(opportunity => opportunity.owner === ownerFilter).map(opportunity => opportunity.id)
  ), [opportunityOwners, ownerFilter]);

  const productOptions = useMemo(() => {
    const products = new Map<string, string>();
    for (const invoice of list) for (const item of invoice.line_items) {
      if (item.product_id) products.set(item.product_id, item.product_name || item.name || item.product_id);
    }
    return Array.from(products, ([value, label]) => ({ value, label })).sort((a,b) => a.label.localeCompare(b.label));
  }, [list]);
  const clientFilterOptions = useMemo(() => {
    const names = new Map(list.map(inv => [inv.client_id, inv.client_name || inv.client_id]));
    return Array.from(names, ([value, label]) => ({ value, label })).filter(item => item.value).sort((a,b) => a.label.localeCompare(b.label));
  }, [list]);
  const filteredList = useMemo(() => {
    let result = ownerFilter ? list.filter(invoice => ownerOpportunityIds.has(invoice.opportunity_id)) : list;
    if (opportunityIdFilter) {
      result = result.filter((invoice) => invoice.opportunity_id === opportunityIdFilter);
    }
    if (invoiceIdFilter) {
      result = result.filter((invoice) => invoice.id === invoiceIdFilter);
    }
    return result.filter(invoice => (selectedMonth === 'all' || invoice.issue_date.slice(0, 7) === selectedMonth) && matchesInvoiceFilters(invoice, {
      statuses: statusFilter, clients: clientFilter, products: productFilter,
      minValue: minValueFilter, maxValue: maxValueFilter,
    }, user?.role === 'admin'));
  }, [list, opportunityIdFilter, invoiceIdFilter, ownerFilter, ownerOpportunityIds, statusFilter, clientFilter, productFilter, minValueFilter, maxValueFilter, user?.role, selectedMonth]);

  const filteredOpportunityName = useMemo(() => {
    if (!opportunityIdFilter) return '';
    return list.find((invoice) => invoice.opportunity_id === opportunityIdFilter)?.name ?? opportunityIdFilter;
  }, [list, opportunityIdFilter]);

  const filteredInvoiceLabel = useMemo(() => {
    if (!invoiceIdFilter) return '';
    const invoice = list.find((item) => item.id === invoiceIdFilter);
    if (!invoice) return invoiceIdFilter;
    return invoice.remito || invoice.name || invoiceIdFilter;
  }, [list, invoiceIdFilter]);

  const navigateToFilteredPage = (path: string, params: Parameters<typeof buildPageUrl>[1]) => {
    setView(null);
    navigate(buildPageUrl(path, params));
  };

  const loadInvoiceFiles = useCallback(async (invoiceId: string) => {
    if (!accessToken) return;
    setLoadingFiles(true);
    try {
      const response = await fetch(`${Environment.VITE_API_URL}/api/v1/invoices/${invoiceId}/files`, {
        headers: { Authorization: `Bearer ${accessToken}` },
        credentials: 'include',
      });
      if (!response.ok) throw new Error(await response.text());
      const data = await response.json();
      const files: InvoiceAttachment[] = Array.isArray(data.files) ? data.files : [];
      setInvoiceFiles(files);
      setView(cur => cur?.id === invoiceId ? {
        ...cur, has_attachments: files.length > 0,
        has_factura_attachment: files.some(file => isInvoiceAttachment(file.file_name)),
        invoice_status: resolveInvoiceStatus(cur.issue_date, files.some(file => isInvoiceAttachment(file.file_name)), cur.invoice_status),
      } : cur);
      setList(cur => cur.map(inv => inv.id === invoiceId ? {
        ...inv, has_attachments: files.length > 0,
        has_factura_attachment: files.some(file => isInvoiceAttachment(file.file_name)),
        invoice_status: resolveInvoiceStatus(inv.issue_date, files.some(file => isInvoiceAttachment(file.file_name)), inv.invoice_status),
      } : inv));
    } catch (error) {
      console.error(error);
      setInvoiceFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (!view) {
      setInvoiceFiles([]);
      return;
    }
    void loadInvoiceFiles(view.id);
  }, [view?.id, loadInvoiceFiles]);

  useEffect(() => {
    openedInvoiceFromUrl.current = false;
  }, [invoiceIdFilter]);

  useEffect(() => {
    if (!invoiceIdFilter || openedInvoiceFromUrl.current || list.length === 0) {
      return;
    }
    const invoice = list.find((item) => item.id === invoiceIdFilter);
    if (invoice) {
      openedInvoiceFromUrl.current = true;
      setView(invoice);
    }
  }, [invoiceIdFilter, list]);

  const applyClientTaxId = async (invoiceId: string, taxId: string) => {
    if (!accessToken) return;
    setUpdatingTaxId(true);
    try {
      const response = await fetch(
        `${Environment.VITE_API_URL}/api/v1/invoices/${invoiceId}/client-tax-id`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
          body: JSON.stringify({ tax_id: taxId }),
        },
      );
      if (!response.ok) throw new Error(await response.text());
      const data = await response.json();
      const nextTaxId = String(data.client_tax_id ?? taxId);
      setList((cur) =>
        cur.map((inv) => (inv.id === invoiceId ? { ...inv, client_tax_id: nextTaxId } : inv)),
      );
      setView((cur) => (cur && cur.id === invoiceId ? { ...cur, client_tax_id: nextTaxId } : cur));
    } catch (error) {
      console.error(error);
    } finally {
      setUpdatingTaxId(false);
      setTaxIdPrompt(null);
    }
  };

  const handleTaxIdPrompt = (
    status: string,
    parsedTaxId: string | null | undefined,
    clientTaxId: string | undefined,
  ) => {
    if (!parsedTaxId) return;
    if (status === 'mismatch') {
      setTaxIdPrompt({
        status: 'mismatch',
        parsedTaxId,
        clientTaxId: clientTaxId ?? '',
      });
      return;
    }
    if (status === 'missing_client') {
      setTaxIdPrompt({
        status: 'missing_client',
        parsedTaxId,
        clientTaxId: '',
      });
    }
  };

  const uploadInvoiceAttachment = async (file: File) => {
    if (!accessToken || !view) return;
    setUploadingAttachment(true);
    setAttachmentErrors([]);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadResponse = await fetch(`${Environment.VITE_API_URL}/api/v1/upload-file`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
        credentials: 'include',
        body: formData,
      });
      if (!uploadResponse.ok) throw new Error(t('invoices.attachments.uploadFailed'));
      const uploadData = await uploadResponse.json();
      const fileUrl = String(uploadData.file_url ?? '');

      const attachResponse = await fetch(
        `${Environment.VITE_API_URL}/api/v1/invoices/${view.id}/files`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
          body: JSON.stringify({ file_name: file.name, file_url: fileUrl }),
        },
      );
      if (!attachResponse.ok) throw new Error(await attachResponse.text());
      const attachData = await attachResponse.json();
      if (attachData.file) {
        setInvoiceFiles((cur) => [...cur, attachData.file]);
        await loadInvoiceFiles(view.id);
      } else {
        await loadInvoiceFiles(view.id);
      }
      handleTaxIdPrompt(
        attachData.tax_id_status,
        attachData.parsed_tax_id,
        attachData.client_tax_id,
      );
    } catch (error) {
      console.error(error);
      setAttachmentErrors([
        error instanceof Error ? error.message : t('invoices.attachments.uploadFailed'),
      ]);
    } finally {
      setUploadingAttachment(false);
    }
  };

  const deleteInvoiceAttachment = async (fileId: string) => {
    if (!accessToken || !view) return;
    try {
      const response = await fetch(
        `${Environment.VITE_API_URL}/api/v1/invoices/${view.id}/files/${fileId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: 'include',
        },
      );
      if (!response.ok) throw new Error(await response.text());
      await loadInvoiceFiles(view.id);
    } catch (error) {
      console.error(error);
    }
  };

  const downloadInvoiceAttachment = async (attachment: InvoiceAttachment) => {
    setDownloadingAttachmentId(attachment.id);
    try {
      await downloadAttachment(
        { fileName: attachment.file_name, filePath: attachment.file_url },
        accessToken,
      );
    } catch (error) {
      console.error(error);
      setAttachmentErrors([t('invoices.attachments.openFailed')]);
    } finally {
      setDownloadingAttachmentId(null);
    }
  };

  const {
    getRootProps: getAttachmentRootProps,
    getInputProps: getAttachmentInputProps,
    isDragActive: isAttachmentDragActive,
  } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    multiple: false,
    maxSize: 50 * 1024 * 1024,
    disabled: uploadingAttachment || invoiceFiles.length >= 10,
    onDrop: (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      const newErrors: string[] = [];
      if (invoiceFiles.length >= 10) {
        newErrors.push(t('invoices.attachments.maxFiles'));
      }
      fileRejections.forEach((rejection) => {
        rejection.errors.forEach((err) => {
          if (err.code === 'file-too-large') {
            newErrors.push(t('invoices.attachments.fileTooLarge'));
          } else if (err.code === 'file-invalid-type') {
            newErrors.push(t('invoices.attachments.pdfOnly'));
          }
        });
      });
      if (newErrors.length) {
        setAttachmentErrors(newErrors);
        return;
      }
      if (acceptedFiles[0]) {
        void uploadInvoiceAttachment(acceptedFiles[0]);
      }
    },
  });

  const invoiceNumberByOpp = useMemo(() => invoiceInstallmentNumbers(list), [list]);

  const cols = useMemo<MRT_ColumnDef<Invoice>[]>(
    () => enableGlobalSearchOnColumns(
      [
      {
        id: 'invoice_number',
        header: t('invoices.invoiceNumber'),
        accessorFn: (row) => {
          const info = invoiceNumberByOpp[row.id];
          return info ? t('invoices.invoiceNumberOf', { num: info.num, total: info.total }) : '';
        },
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        size: 90,
        minSize: 60,
        maxSize: 110,
      },
      {
        accessorKey: 'issue_date',
        header: t('invoices.date'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        size: 110,
        minSize: 90,
        maxSize: 130,
        Cell: ({ cell }) => {
          const v = cell.getValue<string>();
          const x = v ? new Date(v) : null;
          return !v ? '—' : x && !Number.isNaN(x.getTime()) ? formatAppDate(v) : v;
        },
      },
      {
        id: 'client_name',
        accessorFn: (row) => clients.find((c) => c.value === row.client_id)?.label ?? '',
        header: t('invoices.client'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
      },
      {
        accessorKey: 'name',
        header: t('invoices.opportunity'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
      },
      {
        id: 'usd_value',
        header: t('invoices.usdValue'),
        accessorFn: (row) => row.total_amount,
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'right' } },
        Cell: ({ cell }) => {
          const n = Number(cell.getValue<number>());
          return `USD ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        },
      },
      {
        id: 'paid',
        header: t('invoices.paid'),
        accessorFn: (row) => row.paid_amount,
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'right' } },
        size: 80,
        minSize: 60,
        maxSize: 100,
        Cell: ({ row }) => {
          const paid = row.original.paid_amount;
          if (paid <= 0) return '—';
          const pct = row.original.total_amount > 0 ? Math.round((paid / row.original.total_amount) * 100) : 0;
          return `${pct}%`;
        },
      },
      {
        id: 'n',
        header: t('invoices.items'),
        accessorFn: (row) => row.line_items.length,
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
        size: 70,
        minSize: 50,
        maxSize: 90,
      },
      {
        id: 'has_attachments',
        header: t('invoices.hasAttachments'),
        accessorFn: (row) => row.has_attachments,
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
        size: 120,
        minSize: 90,
        maxSize: 140,
        Cell: ({ cell }) => (cell.getValue<boolean>() ? t('invoices.hasAttachmentsYes') : '—'),
      },
      {
        accessorKey: 'invoice_status',
        header: t('invoices.invoiceStatus'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        Cell: ({ cell }) => statusOptions.find((s) => s.value === cell.getValue<string>())?.label ?? cell.getValue<string>(),
      },
      {
        accessorKey: 'notes',
        header: t('invoices.notes'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
      },
    ],
      ['name', 'notes'],
    ),
    [clients, invoiceNumberByOpp, statusOptions, t, i18n.language]
  );

  const linesSubtotal =
    view?.line_items.reduce((s, l) => s + (Number(l.amount_on_invoice) || 0), 0) ?? 0;

  const linesSubtotalWithIva =
    view?.line_items.reduce(
      (s, l) => s + (Number(l.amount_on_invoice) || 0) * (1 + (Number(l.iva) || 0) / 100),
      0
    ) ?? 0;

  function openInvoiceDetail(invoice: Invoice) {
    setView(invoice);
    setNotesValue(invoice.notes);
    setViewCurrency(invoice.currency || 'ARS');
    setRateOverride(invoice.exchange_rate != null ? Number(invoice.exchange_rate) : null);
  }

  async function saveNotes(invoiceId: string, notes: string) {
    if (!accessToken) return;
    setSavingNotes(true);
    try {
      const h = { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` };
      const response = await fetch(`${Environment.VITE_API_URL}/api/v1/invoices/${invoiceId}/notes`, {
        method: 'PATCH',
        headers: h,
        credentials: 'include',
        body: JSON.stringify({ notes }),
      });
      if (!response.ok) throw new Error(await response.text());
      setList((cur) => cur.map((inv) => (inv.id === invoiceId ? { ...inv, notes } : inv)));
      setView((cur) => (cur && cur.id === invoiceId ? { ...cur, notes } : cur));
    } catch (e) {
      console.error(e);
    } finally {
      setSavingNotes(false);
    }
  }

  async function saveExchangeRate(invoiceId: string, rate: number | null) {
    if (!accessToken) return;
    try {
      const h = { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` };
      const response = await fetch(`${Environment.VITE_API_URL}/api/v1/invoices/${invoiceId}/exchange_rate`, {
        method: 'PATCH',
        headers: h,
        credentials: 'include',
        body: JSON.stringify({ exchange_rate: rate }),
      });
      if (!response.ok) throw new Error(await response.text());
      setList((cur) => cur.map((inv) => (inv.id === invoiceId ? { ...inv, exchange_rate: rate } : inv)));
      setView((cur) => (cur && cur.id === invoiceId ? { ...cur, exchange_rate: rate } : cur));
    } catch (e) {
      console.error(e);
    }
  }

  async function saveCurrency(invoiceId: string, currency: string) {
    if (!accessToken) return;
    try {
      const h = { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` };
      const response = await fetch(`${Environment.VITE_API_URL}/api/v1/invoices/${invoiceId}/currency`, {
        method: 'PATCH',
        headers: h,
        credentials: 'include',
        body: JSON.stringify({ currency }),
      });
      if (!response.ok) throw new Error(await response.text());
      setList((cur) => cur.map((inv) => (inv.id === invoiceId ? { ...inv, currency } : inv)));
      setView((cur) => (cur && cur.id === invoiceId ? { ...cur, currency } : cur));
    } catch (e) {
      console.error(e);
    }
  }

  const [savingInvoiceStatus, setSavingInvoiceStatus] = useState(false);
  const [invoiceStatusError, setInvoiceStatusError] = useState<string | null>(null);
  async function changeInvoiceStatus(invoiceId: string, invoice_status: string) {
    if (!accessToken || !['pending', 'canceled'].includes(invoice_status)) return;
    setSavingInvoiceStatus(true);
    setInvoiceStatusError(null);
    try {
      const response = await fetch(`${Environment.VITE_API_URL}/api/v1/invoices/${invoiceId}/invoice_status`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ invoice_status }),
      });
      if (!response.ok) throw new Error(await response.text());
      const data = await response.json();
      setList(cur => cur.map(inv => inv.id === invoiceId ? { ...inv, invoice_status: data.invoice_status } : inv));
      setView(cur => cur?.id === invoiceId ? { ...cur, invoice_status: data.invoice_status } : cur);
    } catch {
      setInvoiceStatusError(t('invoices.statusUpdateFailed'));
    } finally { setSavingInvoiceStatus(false); }
  }

  async function sendToAccounting(invoiceId: string) {
    if (!accessToken) return;

    setSendError(null);
    setSendingInvoiceId(invoiceId);

    try {
      const h = { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` };
      const response = await fetch(
        `${Environment.VITE_API_URL}/api/v1/invoices/${invoiceId}/send-to-accounting`,
        {
          method: 'POST',
          headers: h,
          credentials: 'include',
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || response.statusText);
      }

      // Sending to accounting does not change billing completion.

    } catch (error) {
      setSendError(error instanceof Error ? error.message : t('invoices.sendFailed'));
    } finally {
      setSendingInvoiceId(null);
    }
  }

  const executeInvoiceNotesImport = async (updates: Array<{ id: string; notes: string }>) => {
    if (!accessToken) return;
    const h = { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` };
    for (const entry of updates) {
      const response = await fetch(`${Environment.VITE_API_URL}/api/v1/invoices/${entry.id}/notes`, {
        method: 'PATCH',
        headers: h,
        credentials: 'include',
        body: JSON.stringify({ notes: entry.notes }),
      });
      if (!response.ok) {
        setImportNotification({ message: t('common.import.failed'), color: 'red' });
        return;
      }
    }
    setImportNotification({ message: t('common.import.success'), color: 'green' });
    setImportModalOpened(false);
    setAutoImportFile(null);
    setList((cur) =>
      cur.map((inv) => {
        const patch = updates.find((entry) => entry.id === inv.id);
        return patch ? { ...inv, notes: patch.notes } : inv;
      }),
    );
  };

  const handleInvoiceImport = async () => {
    if (!autoImportFile) return;
    try {
      const rawData = await readExcelFile(autoImportFile);
      const existingIds = new Set(list.map((invoice) => invoice.id));
      const split = splitImportRowsById(rawData, existingIds);
      const errors: string[] = [];

      for (let index = 0; index < rawData.length; index += 1) {
        const row = rawData[index];
        const rowId = pickRowId(row);
        if (rowId && !existingIds.has(rowId)) {
          errors.push(t('common.import.idNotFound', { line: index + 2, id: rowId }));
        }
      }

      if (split.creates.length > 0) {
        errors.push(t('invoices.importUpdatesOnly'));
      }

      const updates: Array<{ id: string; notes: string }> = [];
      for (const entry of split.updates) {
        const line = rawData.indexOf(entry.row) + 2;
        const notesValue = readRowCell(entry.row, t('invoices.notes'), 'Notes');
        if (notesValue === undefined) {
          errors.push(`${line}: ${t('invoices.notes')}`);
          continue;
        }
        updates.push({ id: entry.id, notes: String(notesValue) });
      }

      if (errors.length > 0) {
        setImportNotification({ message: t('common.import.someFailed'), color: 'red' });
        setImportErrors(errors);
        return;
      }

      if (updates.length === 0) {
        setImportNotification({ message: t('common.import.someFailed'), color: 'red' });
        setImportErrors([t('invoices.importNoRows')]);
        return;
      }

      setImportConfirm({
        updateCount: updates.length,
        run: () => executeInvoiceNotesImport(updates),
      });
    } catch (error) {
      console.error(error);
      setImportNotification({ message: t('common.import.processFailed'), color: 'red' });
    }
  };

  const openImportModal = () => {
    setImportModalOpened(true);
    setAutoImportFile(null);
    setImportErrors([]);
    setImportNotification(null);
  };

  const { table, exportModal, exportButton } = useExportableTable(
    {
      columns: cols,
      data: filteredList,
      localization: mrtLocalization,
      defaultColumn: { size: 40, minSize: 30, maxSize: 600 },
      enableColumnFilters: true,
      enableSorting: true,
      enableFullScreenToggle: true,
      enableGrouping: false,
      enableColumnActions: false,
      enablePagination: true,
      enableBottomToolbar: true,
      enableRowVirtualization: false,
      initialState: { density: 'sm' as MRT_DensityState, showGlobalFilter: true, pagination: { pageSize: 20, pageIndex: 0 } },
      mantineTableContainerProps: mrtTableContainerProps,
      mantineTableProps: { sx: tableSx },
      mantineTableBodyRowProps: ({ row }: { row: MRT_Row<Invoice> }) => ({
        onClick: () => openInvoiceDetail(row.original),
        style: { cursor: 'pointer' },
      }),
    },
    {
      idField: 'id',
      filenamePrefix: 'invoices',
      sheetName: 'Invoices',
      getCellValue: (row, columnId) => {
        if (columnId === 'invoice_number') {
          const info = invoiceNumberByOpp[row.id];
          return info ? t('invoices.invoiceNumberOf', { num: info.num, total: info.total }) : '';
        }
        if (columnId === 'client_name') {
          return clients.find((c) => c.value === row.client_id)?.label ?? '';
        }
        if (columnId === 'usd_value') {
          return row.total_amount;
        }
        if (columnId === 'paid') {
          if (row.paid_amount <= 0) return '';
          return row.total_amount > 0 ? Math.round((row.paid_amount / row.total_amount) * 100) : 0;
        }
        if (columnId === 'n') return row.line_items.length;
        if (columnId === 'has_attachments') return row.has_attachments ? t('invoices.hasAttachmentsYes') : '';
        if (columnId === 'invoice_status') {
          return statusOptions.find((s) => s.value === row.invoice_status)?.label ?? row.invoice_status;
        }
        if (columnId === 'issue_date') {
          const x = row.issue_date ? new Date(row.issue_date) : null;
          return x && !Number.isNaN(x.getTime()) ? formatAppDate(row.issue_date) : row.issue_date;
        }
        return row[columnId as keyof Invoice];
      },
      onOpenImport: openImportModal,
    },
  );

  const ownerSelect = (
    <Select
        label={t('opportunity.filters.owner')}
        className="invoice-owner-filter"
        placeholder={t('opportunity.filters.allOwners')}
        data={ownerOptions}
        value={ownerFilter}
        onChange={setOwnerFilter}
        searchable
        clearable
        error={ownerLoadError ? t('invoices.ownerFilterLoadFailed') : undefined}
      />
  );

  const importModal = (
    <TableExcelImportModal
      opened={importModalOpened}
      onClose={() => setImportModalOpened(false)}
      onImport={handleInvoiceImport}
      file={autoImportFile}
      onFileChange={(file) => setAutoImportFile(file)}
      errors={importErrors}
      notification={importNotification}
      hint={t('invoices.importNotesHint')}
      showImportRules={false}
    />
  );

  return (
    <>
      <Group position="apart" mb="md">
        <Group spacing="lg" className="invoice-title-month">
          <Title order={1}>{t('invoices.title')}</Title>
          <InvoiceMonthFilter value={selectedMonth} onChange={setSelectedMonth} label={t('invoices.kanban.paymentDate')}/>
        </Group>
        <Group spacing="sm" className="invoice-header-actions">
        <Button variant="subtle" disabled={!ownerFilter && !statusFilter.length && !clientFilter.length && !productFilter.length && minValueFilter === '' && maxValueFilter === '' && selectedMonth === invoiceMonthValue(new Date()) && !table.getState().globalFilter}
          onClick={() => { setOwnerFilter(null); setStatusFilter([]); setClientFilter([]); setProductFilter([]); setMinValueFilter(''); setMaxValueFilter(''); setSelectedMonth(invoiceMonthValue(new Date())); table.setGlobalFilter(''); }}>
          {t('invoices.filters.clear')}
        </Button>
        <SegmentedControl value={viewMode} onChange={value => setViewMode(value as 'table' | 'kanban')} data={[
          { value: 'table', label: t('invoices.kanban.tableView') },
          { value: 'kanban', label: t('invoices.kanban.viewLabel') },
        ]} />
        </Group>
      </Group>
      {loadError && (
        <Text color="red" size="sm" mb="sm">
          {loadError}
        </Text>
      )}
      {opportunityIdFilter && (
        <PageFilterBanner
          message={t('invoices.filteredByOpportunity', { name: filteredOpportunityName })}
          onClear={() => navigate('/invoices')}
        />
      )}
      {invoiceIdFilter && (
        <PageFilterBanner
          message={t('invoices.filteredByInvoice', { name: filteredInvoiceLabel })}
          onClear={() => navigate('/invoices')}
        />
      )}
      <div className={`invoice-page-filters invoice-filters-shared ${user?.role === 'admin' ? 'invoice-filters-with-values' : ''}`}>
        {ownerSelect}
        <MultiSelect label={t('invoices.invoiceStatus')} placeholder={t('invoices.filters.allStatuses')}
          data={statusOptions} value={statusFilter} onChange={setStatusFilter} clearable searchable />
        <MultiSelect label={t('invoices.client')} placeholder={t('invoices.filters.allClients')}
          data={clientFilterOptions} value={clientFilter} onChange={setClientFilter} clearable searchable />
        <MultiSelect label={t('invoices.filters.products')} placeholder={t('invoices.filters.allProducts')}
          data={productOptions} value={productFilter} onChange={setProductFilter} clearable searchable />
        {user?.role === 'admin' && <div className="invoice-value-range">
          <Text size="sm" weight={500} id="invoice-value-range-label">{t('invoices.filters.valueRange')}</Text>
          <Group position="apart" spacing="xs" className="invoice-value-range-values">
            <Text size="xs">{formatRangeValue(valueRange[0])}</Text>
            <Text size="xs">{formatRangeValue(valueRange[1])}</Text>
          </Group>
          <RangeSlider min={0} max={valueRangeMax} step={0.01} precision={2} minRange={0}
            value={valueRange} onChange={([min, max]) => {
              setMinValueFilter(min === 0 ? '' : min);
              setMaxValueFilter(max === valueRangeMax ? '' : max);
            }}
            label={formatRangeValue}
            thumbFromLabel={t('invoices.filters.minValue')} thumbToLabel={t('invoices.filters.maxValue')}
            aria-labelledby="invoice-value-range-label" color="teal" size="sm" />
        </div>}


      </div>
      {viewMode === 'table' ? <div style={tableShellStyle}>
        <InvoiceOpportunityTable allInvoices={list}
          invoices={table.getFilteredRowModel().rows.map(row => row.original)}
          owners={ownerNamesByOpportunity} showAmounts={user?.role === 'admin'}
          invoiceNumbers={invoiceNumberByOpp} onOpen={openInvoiceDetail}
          toolbar={<Group spacing="xs">
            <Button size="xs" variant="light" onClick={openImportModal}>{t('common.import.excel')}</Button>
            {exportButton}
          </Group>}
          searchControl={<TextInput placeholder={t('invoices.kanban.search')} aria-label={t('invoices.kanban.search')}
            icon={<IconSearch size={18}/>} className="invoice-table-search"
            value={String(table.getState().globalFilter || '')}
            onChange={event => table.setGlobalFilter(event.currentTarget.value)} />} />
        {exportModal}{importModal}
      </div> : <>
        <InvoiceKanbanView selectedMonth={selectedMonth}
          toolbarFilters={<TextInput icon={<IconSearch size={18}/>} label={t('invoices.kanban.search')} placeholder={t('invoices.kanban.search')}
            value={String(table.getState().globalFilter || '')}
            onChange={event => table.setGlobalFilter(event.currentTarget.value)} />}
          showAmounts={user?.role === 'admin'} invoices={table.getFilteredRowModel().rows.map(row => ({ ...row.original, owner_name: ownerNamesByOpportunity.get(row.original.opportunity_id) || '' }))}
          onOpen={openInvoiceDetail} invoiceNumbers={invoiceNumberByOpp} />
      </>}

      <ImportConfirmModal
        opened={importConfirm != null}
        newCount={0}
        updateCount={importConfirm?.updateCount ?? 0}
        entityKey="invoices"
        onCancel={() => setImportConfirm(null)}
        onConfirm={() => {
          const run = importConfirm?.run;
          setImportConfirm(null);
          void run?.();
        }}
      />

      <Modal opened={view != null} onClose={() => setView(null)} centered title={t('invoices.detailsTitle')} size="xl">
        {view && (
          <ScrollArea>
            <div style={{ padding: '0.5rem 0' }}>
              <Text size="sm" mb="xs">
                <strong>{t('invoices.remito')}:</strong> {view.remito || '—'}
              </Text>
              <Text size="sm" mb="xs">
                <strong>{t('invoices.client')}:</strong>{' '}
                <EntityLink
                  label={
                    view.client_name ||
                    clients.find((c) => c.value === view.client_id)?.label ||
                    view.client_id
                  }
                  onClick={() => navigateToFilteredPage('/clients', { clientId: view.client_id })}
                />
              </Text>
              <Text size="sm" mb="xs">
                <strong>{t('invoices.cuit')}:</strong> {view.client_tax_id || '—'}
              </Text>
              <Text size="sm" mb="xs">
                <strong>{t('invoices.ivaCondition')}:</strong> {view.client_iva ? view.client_iva.replace(/_/g, ' ') : '—'}
              </Text>
              <Text size="sm" mb="xs">
                <strong>{t('invoices.address')}:</strong>{' '}
                {[view.client_address, view.client_city, view.client_state, view.client_zipcode, view.client_country]
                  .filter(Boolean)
                  .join(', ') || '—'}
              </Text>
              <Text size="sm" mb="xs">
                <strong>{t('invoices.opportunity')}:</strong>{' '}
                <EntityLink
                  label={view.name}
                  onClick={() =>
                    navigateToFilteredPage('/opportunity', { opportunityId: view.opportunity_id })
                  }
                />
              </Text>
              <Text size="sm" mb="xs">
                <strong>{t('invoices.issueDate')}:</strong>{' '}
                {(() => {
                  const x = new Date(view.issue_date);
                  return Number.isNaN(x.getTime()) ? view.issue_date : formatAppDate(view.issue_date);
                })()}
              </Text>
              <Text size="sm" mb="xs">
                <strong>{t('invoices.invoiceStatus')}:</strong> <InvoiceStatusBadge status={view.invoice_status} paymentDate={view.issue_date} />
                {view.paid_amount > 0 && ` ${t('invoices.paidPercent', {
                  percent: view.total_amount > 0 ? Math.round((view.paid_amount / view.total_amount) * 100) : 0,
                  amount: usdAmount(view.paid_amount),
                })}`}
              </Text>
              <Select
                label={t('invoices.changeStatus')}
                placeholder={t('invoices.chooseStatus')}
                data={statusOptions.filter(option => ['pending', 'canceled'].includes(option.value))}
                value={view.invoice_status === 'canceled' ? 'canceled' : null}
                onChange={value => { if (value) void changeInvoiceStatus(view.id, value); }}
                disabled={savingInvoiceStatus}
                error={invoiceStatusError}
                description={t('invoices.automaticStatusHint')}
                mb="sm"
              />
              <Group mb="sm">
                <Button
                  onClick={() => sendToAccounting(view.id)}
                  loading={sendingInvoiceId === view.id}
                >
                  {t('invoices.sendToAccounting')}
                </Button>
                {sendError && (
                  <Text color="red" size="sm">
                    {sendError}
                  </Text>
                )}
              </Group>
              <Textarea
                label={t('invoices.notes')}
                placeholder={t('invoices.notesPlaceholder')}
                minRows={2}
                autosize
                value={notesValue}
                onChange={(e) => setNotesValue(e.currentTarget.value)}
                mb="sm"
              />
              <Button
                variant="light"
                size="xs"
                mb="sm"
                loading={savingNotes}
                disabled={notesValue === view.notes}
                onClick={() => saveNotes(view.id, notesValue)}
              >
                {t('invoices.saveNotes')}
              </Button>
              <Divider my="sm" />
              <Text weight={600} mb="xs">
                {t('invoices.attachments.title')}
              </Text>
              <div
                {...getAttachmentRootProps()}
                style={{
                  border: '2px dashed #ccc',
                  borderRadius: 8,
                  padding: '1rem',
                  textAlign: 'center',
                  cursor: uploadingAttachment || invoiceFiles.length >= 10 ? 'not-allowed' : 'pointer',
                  opacity: uploadingAttachment || invoiceFiles.length >= 10 ? 0.6 : 1,
                  marginBottom: '0.75rem',
                }}
              >
                <input {...getAttachmentInputProps()} />
                {uploadingAttachment ? (
                  <Group position="center" spacing="xs">
                    <Loader size="sm" />
                    <Text size="sm">{t('invoices.attachments.uploading')}</Text>
                  </Group>
                ) : (
                  <Text size="sm" color="dimmed">
                    {isAttachmentDragActive
                      ? t('invoices.attachments.dropActive')
                      : t('invoices.attachments.dropPdf')}
                  </Text>
                )}
              </div>
              {attachmentErrors.length > 0 && (
                <Stack spacing={4} mb="sm">
                  {attachmentErrors.map((err) => (
                    <Text key={err} color="red" size="sm">
                      {err}
                    </Text>
                  ))}
                </Stack>
              )}
              {loadingFiles ? (
                <Group position="center" mb="sm">
                  <Loader size="sm" />
                </Group>
              ) : invoiceFiles.length > 0 ? (
                <Table highlightOnHover withBorder striped mb="sm">
                  <thead>
                    <tr>
                      <th>{t('invoices.attachments.fileName')}</th>
                      <th><DateColumnHeader label={t('invoices.attachments.uploadedAt')} language={i18n.language} dateTime/></th>
                      <th>{t('invoices.attachments.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceFiles.map((attachment) => (
                      <tr key={attachment.id}>
                        <td>{attachment.file_name}</td>
                        <td>
                          {attachment.uploaded_at
                            ? formatAppDateTime(attachment.uploaded_at)
                            : '—'}
                        </td>
                        <td>
                          <AttachmentActionButtons
                            onPreview={() =>
                              openPreview({
                                fileName: attachment.file_name,
                                filePath: attachment.file_url,
                              })
                            }
                            onDownload={() => void downloadInvoiceAttachment(attachment)}
                            onRemove={() => void deleteInvoiceAttachment(attachment.id)}
                            downloadLoading={downloadingAttachmentId === attachment.id}
                            showRemove
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Text size="sm" color="dimmed" mb="sm">
                  {t('invoices.attachments.empty')}
                </Text>
              )}
              <Select
                label={t('invoices.currency')}
                value={viewCurrency}
                onChange={(v) => {
                  const next = v ?? 'ARS';
                  setViewCurrency(next);
                  if (view && next !== view.currency) {
                    saveCurrency(view.id, next);
                  }
                }}
                data={[
                  { value: 'USD', label: 'USD' },
                  { value: 'ARS', label: 'ARS' },
                ]}
                mb="sm"
                size="xs"
                style={{ maxWidth: 160 }}
              />
              <Text size="sm" mb="xs">
                <strong>{t('invoices.usdValue')}:</strong> {usdAmount(Number(view.total_amount))}
              </Text>
              <Text size="sm" mb="xs">
                <strong>{t('invoices.usdValueWithIva')}:</strong> {usdAmount(linesSubtotalWithIva)}
              </Text>
              <Text size="sm" mb="xs">
                <strong>{t('invoices.convertedValueWithIva')}:</strong> {convertedAmount(linesSubtotalWithIva, viewCurrency, effectiveRate)}
              </Text>
              {viewCurrency !== 'USD' && (
                <Group spacing="xs" align="flex-end" mb="sm">
                  <NumberInput
                    key={`rate-${view.id}-${isManualRate ? 'm' : 'b'}-${effectiveRate ?? ''}`}
                    label={t('invoices.exchangeRate')}
                    description={buildRateDescription(t, viewCurrency, effectiveRate, isManualRate, issueRateFecha, bnaFetchAttempted, isIssueDateInFuture(view.issue_date))}
                    defaultValue={effectiveRate ?? undefined}
                    onBlur={(e) => {
                      const raw = e.currentTarget.value.trim();
                      if (raw === '') {
                        if (isManualRate) {
                          setRateOverride(null);
                          saveExchangeRate(view.id, null);
                        }
                        return;
                      }
                      const next = Number(raw.replace(',', '.'));
                      if (!Number.isFinite(next) || next <= 0) return;
                      if (next === rateOverride) return;
                      setRateOverride(next);
                      saveExchangeRate(view.id, next);
                    }}
                    precision={4}
                    min={0}
                    step={0.01}
                    size="xs"
                    style={{ maxWidth: 260 }}
                  />
                  {isManualRate && (
                    <Button
                      variant="light"
                      size="xs"
                      onClick={() => {
                        setRateOverride(null);
                        saveExchangeRate(view.id, null);
                      }}
                    >
                      {t('invoices.reset')}
                    </Button>
                  )}
                </Group>
              )}
              <Text size="xs" color="dimmed" mb="md">
                {t('invoices.lineAmountsSum', { amount: usdAmount(linesSubtotal) })}
              </Text>
              <Divider my="sm" />
              <Text weight={600} mb="md">
                {t('invoices.productsAndPayments')}
              </Text>
              <Stack spacing="lg">
                {view.line_items.map((line) => (
                  <div key={`${line.opportunity_product_id}-${line.parcela_num_on_invoice ?? 'av'}-${line.amount_on_invoice}`}>
                    <Group position="apart" align="flex-start" noWrap mb={6}>
                      <div style={{ flex: 1 }}>
                        <Text size="sm" weight={500}>
                          <EntityLink
                            label={line.product_name}
                            onClick={() =>
                              navigateToFilteredPage('/products', { productId: line.product_id })
                            }
                          />
                        </Text>
                        <Text size="xs" color="dimmed">
                          {t('invoices.qtyUnitDeal', { qty: line.quantity, amount: usdAmount(line.deal_price) })}
                          {line.discount ? <>{' · '}{t('invoices.discount', { percent: line.discount })}</> : null}
                          {' · '}{line.iva ? t('invoices.ivaPercent', { percent: Number(line.iva).toFixed(2) }) : t('invoices.ivaZero')}
                        </Text>
                        <Text size="xs" color="dimmed">
                          {t('invoices.lineTotal', { amount: usdAmount(line.line_total) })}
                          {line.iva ? <>{' · '}{t('invoices.lineTotalWithIva', { amount: usdAmount(line.line_total * (1 + line.iva / 100)) })}</> : null}
                        </Text>
                      </div>
                      <Text size="sm" align="right" weight={600}>
                        {t('invoices.onThisInvoice', { amount: usdAmount(line.amount_on_invoice) })}
                        {line.iva ? (
                          <>
                            <br />
                            {t('invoices.withIva', { amount: usdAmount(line.amount_on_invoice * (1 + line.iva / 100)) })}
                          </>
                        ) : null}
                      </Text>
                    </Group>
                    <Text size="xs" mb={4}>
                      <strong>{t('invoices.payment')}:</strong>{' '}
                      {line.payment_type === 'parcelado' ? t('invoices.paymentInstallments') : t('invoices.paymentCash')}
                      {line.data_avista ? <>{' · '}{t('invoices.dueDate', { date: formatAppDate(line.data_avista) })}</> : null}
                      {line.parcela_num_on_invoice != null ? <>{' · '}{t('invoices.installmentOnBill', {
                        num: line.parcela_num_on_invoice,
                        ofTotal: line.installments_count ? t('invoices.installmentOfTotal', { total: line.installments_count }) : '',
                      })}</> : null}
                    </Text>
                    {line.installments && line.installments.length > 0 && (
                      <Stack spacing={4} pl="md" mt="xs">
                        <Text size="xs" weight={600}>
                          {t('invoices.installmentSchedule')}
                        </Text>
                        {line.installments.map((p) => (
                          <Group key={p.parcela_num} position="apart" spacing="xs" noWrap>
                            <Text size="xs">
                              {t('invoices.installmentLine', {
                                num: p.parcela_num,
                                date: formatAppDate(p.data),
                              })}
                            </Text>
                            <Text size="xs" align="right">
                              {usdAmount(p.amount)}
                            </Text>
                          </Group>
                        ))}
                      </Stack>
                    )}
                  </div>
                ))}
              </Stack>
            </div>
          </ScrollArea>
        )}
      </Modal>

      <Modal
        opened={taxIdPrompt != null}
        onClose={() => setTaxIdPrompt(null)}
        centered
        title={
          taxIdPrompt?.status === 'mismatch'
            ? t('invoices.taxIdPrompt.mismatchTitle')
            : t('invoices.taxIdPrompt.missingTitle')
        }
      >
        {taxIdPrompt && view && (
          <Stack spacing="md">
            <Text size="sm">
              {taxIdPrompt.status === 'mismatch'
                ? t('invoices.taxIdPrompt.mismatchMessage', {
                    current: taxIdPrompt.clientTaxId || '—',
                    parsed: taxIdPrompt.parsedTaxId,
                  })
                : t('invoices.taxIdPrompt.missingMessage', {
                    parsed: taxIdPrompt.parsedTaxId,
                  })}
            </Text>
            <Group position="right">
              <Button variant="default" onClick={() => setTaxIdPrompt(null)}>
                {taxIdPrompt.status === 'mismatch'
                  ? t('invoices.taxIdPrompt.keepExisting')
                  : t('invoices.taxIdPrompt.skipUpdate')}
              </Button>
              <Button
                loading={updatingTaxId}
                onClick={() => void applyClientTaxId(view.id, taxIdPrompt.parsedTaxId)}
              >
                {taxIdPrompt.status === 'mismatch'
                  ? t('invoices.taxIdPrompt.useParsed')
                  : t('invoices.taxIdPrompt.updateClient')}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
      {previewModal}
    </>
  );
}
